import React, { useLayoutEffect, useRef, useState } from 'react'

import { MapStateData } from '@/lib/mapDataTransforms'

import NSW_SVG from '../../../../public/svg/states/NSW.svg'
import NSW_DOTS_SVG from '../../../../public/svg/states/NSW_dots.svg'
import NT_SVG from '../../../../public/svg/states/NT.svg'
import NZ_SVG from '../../../../public/svg/states/NZ.svg'
import NZ_DOTS_SVG from '../../../../public/svg/states/NZ_dots.svg'
import QLD_SVG from '../../../../public/svg/states/QLD.svg'
import QLD_DOTS_SVG from '../../../../public/svg/states/QLD_dots.svg'
import SA_SVG from '../../../../public/svg/states/SA.svg'
import TAS_SVG from '../../../../public/svg/states/TAS.svg'
import TAS_DOTS_SVG from '../../../../public/svg/states/TAS_dots.svg'
import VIC_SVG from '../../../../public/svg/states/VIC.svg'
import VIC_DOTS_SVG from '../../../../public/svg/states/VIC_dots.svg'
import WA_SVG from '../../../../public/svg/states/WA.svg'
import WA_DOTS_SVG from '../../../../public/svg/states/WA_dots.svg'
import styles from './InteractiveMap.module.css'

interface InteractiveMapProps {
  stateData?: MapStateData
  className?: string
}

interface PopoverState {
  isVisible: boolean
  isAnimatingIn: boolean
  x: number
  y: number
  state: string | null
}

interface StateComponentProps {
  Component: React.ComponentType<React.SVGProps<SVGSVGElement>>
  stateId: string
  x: number
  y: number
  scale: number
  isHovered: boolean
  onMouseEnter: () => void
  onMouseMove: (event: React.MouseEvent) => void
  onMouseLeave: () => void
  onClick?: (event: React.MouseEvent) => void
  onTouchEnd?: (event: React.TouchEvent) => void
}

const statePositions = {
  WA: {
    dotsTransform: 'translate(40, 365)', // Adjust for WA dots
  },
  NT: {},
  QLD: {
    dotsTransform: 'translate(130, 120)', // Adjust for QLD dots
  },
  SA: {},
  NSW: {
    dotsTransform: 'translate(100, 80)', // Adjust for NSW dots
  },
  VIC: {
    dotsTransform: 'translate(20, 0)', // Adjust for VIC dots
  },
  TAS: {
    dotsTransform: 'translate(0, 0)', // Adjust for TAS dots
  },
  NZ: {
    dotsTransform: 'translate(25, 55)', // Current NZ positioning
  },
}

const StateComponent: React.FC<StateComponentProps> = ({
  Component,
  stateId,
  x,
  y,
  scale,
  isHovered,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  onClick,
  onTouchEnd,
}) => {
  return (
    <g
      id={`state-${stateId}`}
      transform={`translate(${x}, ${y}) scale(${scale})`}
      onMouseEnter={onMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      onTouchEnd={onTouchEnd}
    >
      <Component />
      {isHovered && <style>{`#state-${stateId} path { fill: #567B9D !important; }`}</style>}
    </g>
  )
}

const DotsOverlay: React.FC<{
  stateId: string
  x: number
  y: number
  scale: number
  isHovered: boolean
  DotsComponent: React.ComponentType<React.SVGProps<SVGSVGElement>>
  dotsTransform: string
}> = ({ stateId, x, y, scale, isHovered, DotsComponent, dotsTransform }) => {
  return (
    <g
      className={`dots-${stateId}`}
      transform={`translate(${x}, ${y}) scale(${scale}) ${dotsTransform}`}
    >
      <DotsComponent />
      <style>{`
        .dots-${stateId} path {
          fill: ${isHovered ? '#21FFA6' : '#6EE4FF'} !important;
          transition: fill 0.2s ease;
        }
      `}</style>
    </g>
  )
}

export const InteractiveMap: React.FC<InteractiveMapProps> = ({
  stateData = {},
  className = '',
}) => {
  // Utility to check if device is mobile
  const [isMobile, setIsMobile] = useState(false)
  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const [popover, setPopover] = useState<PopoverState>({
    isVisible: false,
    isAnimatingIn: false,
    x: 0,
    y: 0,
    state: null,
  })

  const mapRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const animationTimeoutRef = useRef<number | null>(null)
  const [pendingStateId, setPendingStateId] = useState<string | null>(null)
  const debounceTimeoutRef = useRef<number | null>(null)

  // Note: Dots are now handled per-state, no global state needed

  const showPopoverForState = (stateId: string) => {
    // Clear any existing debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    // If already showing this state, don't restart
    if (popover.isVisible && popover.state === stateId) {
      return
    }

    let x = 0
    let y = 0
    // For desktop, we'll calculate position in useLayoutEffect
    // For mobile, we use percentage positioning in CSS
    setPopover({
      isVisible: true,
      isAnimatingIn: false,
      x,
      y,
      state: stateId,
    })
    setPendingStateId(stateId)
  }

  useLayoutEffect(() => {
    if (!pendingStateId || !popoverRef.current) return

    if (isMobile) {
      // On mobile, just trigger animation
      setPopover((prev) => ({ ...prev, isAnimatingIn: true }))
      setPendingStateId(null)
      return
    }

    const element = document.getElementById(`state-${pendingStateId}`)
    if (!element || !mapRef.current) return

    const stateRect = element.getBoundingClientRect()
    const popoverRect = popoverRef.current.getBoundingClientRect()
    const popoverX = stateRect.left - popoverRect.width + 30
    const popoverY = stateRect.top + stateRect.height / 2 - popoverRect.height / 2

    setPopover((prev) => ({
      ...prev,
      x: popoverX,
      y: popoverY,
    }))

    // Trigger fade-in
    setTimeout(() => {
      setPopover((prev) => ({
        ...prev,
        isAnimatingIn: true,
      }))
    }, 10)

    setPendingStateId(null)
  }, [pendingStateId, isMobile])

  // Desktop hover handlers
  const handleStateMouseEnter = (stateId: string) => () => {
    if (isMobile) return
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current)
      animationTimeoutRef.current = null
    }
    if (popover.isVisible && popover.state !== stateId) {
      setPopover((prev) => ({ ...prev, isAnimatingIn: false }))
      setTimeout(() => {
        showPopoverForState(stateId)
      }, 150)
    } else if (!popover.isVisible) {
      showPopoverForState(stateId)
    }
  }
  const handleStateMouseMove = () => {}
  const handleStateMouseLeave = () => {
    if (isMobile) return
    setPopover((prev) => ({ ...prev, isAnimatingIn: false }))
    animationTimeoutRef.current = window.setTimeout(() => {
      setPopover({
        isVisible: false,
        isAnimatingIn: false,
        x: 0,
        y: 0,
        state: null,
      })
    }, 300)
  }

  // Mobile tap/click handler
  const handleStateClick = (stateId: string) => (event: React.MouseEvent) => {
    if (!isMobile) return
    event.preventDefault()
    event.stopPropagation()
    // Always show the popover for the clicked state
    showPopoverForState(stateId)
  }

  // Mobile touch handler - more reliable with touchend
  const handleStateTouchEnd = (stateId: string) => (event: React.TouchEvent) => {
    if (!isMobile) return
    event.preventDefault()
    event.stopPropagation()
    // Always show the popover for the touched state
    showPopoverForState(stateId)
  }

  // Close popover for mobile (close button or backdrop)
  const handleClosePopover = (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    setPopover({
      isVisible: false,
      isAnimatingIn: false,
      x: 0,
      y: 0,
      state: null,
    })
  }

  // Render popover content (add close button for mobile)
  const renderPopoverContent = () => {
    if (!popover.state || !popover.isVisible) {
      return null
    }
    const currentStateData = stateData[popover.state]
    if (!currentStateData) {
      return null
    }
    if (!mapRef.current) {
      return null
    }
    let sectionsToRender: Array<{ title: string; cities: [string, string[]][] }> = []
    if (popover.state === 'NSW') {
      const actData = stateData['ACT']
      if (actData) {
        const actCities = Object.entries(actData.cities)
          .sort(([cityA], [cityB]) => cityA.localeCompare(cityB))
          .map(
            ([cityName, advisers]) =>
              [cityName, [...advisers].sort((a, b) => a.localeCompare(b))] as [string, string[]]
          )
        sectionsToRender.push({
          title: isMobile ? 'ACT' : 'AUSTRALIAN CAPITAL TERRITORY',
          cities: actCities,
        })
      }
      const nswCities = Object.entries(currentStateData.cities)
        .sort(([cityA], [cityB]) => cityA.localeCompare(cityB))
        .map(
          ([cityName, advisers]) =>
            [cityName, [...advisers].sort((a, b) => a.localeCompare(b))] as [string, string[]]
        )
      sectionsToRender.push({
        title: 'NEW SOUTH WALES',
        cities: nswCities,
      })
    } else {
      const sortedCities = Object.entries(currentStateData.cities)
        .sort(([cityA], [cityB]) => cityA.localeCompare(cityB))
        .map(
          ([cityName, advisers]) =>
            [cityName, [...advisers].sort((a, b) => a.localeCompare(b))] as [string, string[]]
        )
      sectionsToRender.push({
        title: currentStateData.name.toUpperCase(),
        cities: sortedCities,
      })
    }
    const allSortedCities = sectionsToRender.flatMap((section) => section.cities)
    const midpoint = Math.ceil(allSortedCities.length / 2)
    const leftColumn = allSortedCities.slice(0, midpoint)
    const rightColumn = allSortedCities.slice(midpoint)

    // Mobile: add close button and adjust style
    return (
      <>
        {isMobile && popover.isVisible && (
          <div
            role="button"
            tabIndex={0}
            aria-label="Close popover"
            style={{
              position: 'fixed',
              left: 0,
              top: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.2)',
              zIndex: 999,
            }}
            onClick={(e) => handleClosePopover(e)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleClosePopover()
            }}
          />
        )}
        <div
          ref={popoverRef}
          style={{
            position: isMobile ? 'absolute' : 'fixed',
            left: isMobile ? '50%' : popover.x,
            top: isMobile ? '50%' : popover.y,
            transform: isMobile ? 'translate(-50%, -50%)' : undefined,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            pointerEvents: isMobile ? 'auto' : 'none',
            maxWidth: isMobile ? '90vw' : undefined,
          }}
          role={isMobile ? 'dialog' : undefined}
          tabIndex={isMobile ? 0 : undefined}
          aria-modal={isMobile ? 'true' : undefined}
          onClick={
            isMobile
              ? (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  return false
                }
              : undefined
          }
          onKeyDown={
            isMobile
              ? (e) => {
                  if (e.key === 'Escape') handleClosePopover()
                }
              : undefined
          }
        >
          <div
            className={`${styles.popover} ${popover.isAnimatingIn ? styles.visible : ''}`}
            style={isMobile ? { pointerEvents: 'auto' } : {}}
            onClick={
              isMobile
                ? (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    return false
                  }
                : undefined
            }
            onKeyDown={
              isMobile
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      e.stopPropagation()
                      return false
                    }
                  }
                : undefined
            }
            role={isMobile ? 'button' : undefined}
            tabIndex={isMobile ? 0 : undefined}
            aria-label={isMobile ? 'Popover content area' : undefined}
          >
            {isMobile && (
              <button
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1001,
                  background: 'transparent',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                }}
                aria-label="Close"
                onClick={(e) => handleClosePopover(e)}
              >
                ×
              </button>
            )}
            {popover.state === 'NSW' ? (
              <>
                {sectionsToRender.map((section, sectionIndex) => {
                  const isNSWSection = section.title === 'NEW SOUTH WALES'
                  if (isNSWSection) {
                    const midpoint = Math.ceil(section.cities.length / 2)
                    const leftColumn = section.cities.slice(0, midpoint)
                    const rightColumn = section.cities.slice(midpoint)
                    return (
                      <div
                        key={sectionIndex}
                        style={{
                          marginBottom: sectionIndex < sectionsToRender.length - 1 ? '20px' : '0',
                        }}
                      >
                        <div className={styles['popover-header']}>{section.title}</div>
                        <div className={styles['popover-content']}>
                          <div className={styles['column']}>
                            {leftColumn.map(([cityName, advisers]) => (
                              <div key={cityName} className={styles['city-group']}>
                                <h4 className={styles['city-name']}>{cityName}</h4>
                                {advisers.map((adviser, index) => (
                                  <p key={index} className={styles['adviser-name']}>
                                    {adviser}
                                  </p>
                                ))}
                              </div>
                            ))}
                          </div>
                          <div className={styles['column']}>
                            {rightColumn.map(([cityName, advisers]) => (
                              <div key={cityName} className={styles['city-group']}>
                                <h4 className={styles['city-name']}>{cityName}</h4>
                                {advisers.map((adviser, index) => (
                                  <p key={index} className={styles['adviser-name']}>
                                    {adviser}
                                  </p>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )
                  } else {
                    return (
                      <div
                        key={sectionIndex}
                        style={{
                          marginBottom: sectionIndex < sectionsToRender.length - 1 ? '20px' : '0',
                        }}
                      >
                        <div className={styles['popover-header']}>{section.title}</div>
                        <div className={styles['popover-content']}>
                          <div className={styles['column']}>
                            {section.cities.map(([cityName, advisers]) => (
                              <div key={cityName} className={styles['city-group']}>
                                <h4 className={styles['city-name']}>{cityName}</h4>
                                {advisers.map((adviser, index) => (
                                  <p key={index} className={styles['adviser-name']}>
                                    {adviser}
                                  </p>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )
                  }
                })}
              </>
            ) : (
              <>
                <div className={styles['popover-header']}>
                  {currentStateData.name.toUpperCase()}
                </div>
                <div className={styles['popover-content']}>
                  <div className={styles['column']}>
                    {leftColumn.map(([cityName, advisers]) => (
                      <div key={cityName} className={styles['city-group']}>
                        <h4 className={styles['city-name']}>{cityName}</h4>
                        {advisers.map((adviser, index) => (
                          <p key={index} className={styles['adviser-name']}>
                            {adviser}
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className={styles['column']}>
                    {rightColumn.map(([cityName, advisers]) => (
                      <div key={cityName} className={styles['city-group']}>
                        <h4 className={styles['city-name']}>{cityName}</h4>
                        {advisers.map((adviser, index) => (
                          <p key={index} className={styles['adviser-name']}>
                            {adviser}
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          {!isMobile && (
            <div
              className={`${styles['connector-line']} ${popover.isAnimatingIn ? styles.visible : ''}`}
              style={{
                width: '50px',
                height: '2px',
                backgroundColor: '#E94F35',
                zIndex: 999,
              }}
            ></div>
          )}
        </div>
      </>
    )
  }

  return (
    <div
      ref={mapRef}
      className={`${styles['interactive-map']} ${className}`}
      style={{ overflow: 'visible' }}
      role={isMobile ? 'region' : undefined}
      tabIndex={isMobile ? 0 : undefined}
      aria-label={isMobile ? 'Interactive map' : undefined}
      onKeyDown={
        isMobile
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleClosePopover()
              }
            }
          : undefined
      }
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 1059 765"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block' }}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        {/* STATES LAYER - All states render first */}
        {/* Western Australia - far left */}
        <StateComponent
          Component={WA_SVG}
          stateId="WA"
          x={-1}
          y={96}
          scale={1}
          isHovered={popover.state === 'WA'}
          onMouseEnter={handleStateMouseEnter('WA')}
          onMouseMove={handleStateMouseMove}
          onMouseLeave={handleStateMouseLeave}
          onClick={isMobile ? handleStateClick('WA') : undefined}
          onTouchEnd={isMobile ? handleStateTouchEnd('WA') : undefined}
        />
        {/* Northern Territory - top center */}
        <StateComponent
          Component={NT_SVG}
          stateId="NT"
          x={318}
          y={37}
          scale={1}
          isHovered={popover.state === 'NT'}
          onMouseEnter={handleStateMouseEnter('NT')}
          onMouseMove={handleStateMouseMove}
          onMouseLeave={handleStateMouseLeave}
          onClick={isMobile ? handleStateClick('NT') : undefined}
          onTouchEnd={isMobile ? handleStateTouchEnd('NT') : undefined}
        />
        {/* Queensland - top right */}
        <StateComponent
          Component={QLD_SVG}
          stateId="QLD"
          x={494}
          y={22}
          scale={1}
          isHovered={popover.state === 'QLD'}
          onMouseEnter={handleStateMouseEnter('QLD')}
          onMouseMove={handleStateMouseMove}
          onMouseLeave={handleStateMouseLeave}
          onClick={isMobile ? handleStateClick('QLD') : undefined}
          onTouchEnd={isMobile ? handleStateTouchEnd('QLD') : undefined}
        />
        {/* South Australia - middle, adjusted positioning */}
        <StateComponent
          Component={SA_SVG}
          stateId="SA"
          x={318}
          y={353}
          scale={1}
          isHovered={popover.state === 'SA'}
          onMouseEnter={handleStateMouseEnter('SA')}
          onMouseMove={handleStateMouseMove}
          onMouseLeave={handleStateMouseLeave}
          onClick={isMobile ? handleStateClick('SA') : undefined}
          onTouchEnd={isMobile ? handleStateTouchEnd('SA') : undefined}
        />
        {/* New South Wales - right side */}
        <StateComponent
          Component={NSW_SVG}
          stateId="NSW"
          x={552}
          y={397}
          scale={1}
          isHovered={popover.state === 'NSW'}
          onMouseEnter={handleStateMouseEnter('NSW')}
          onMouseMove={handleStateMouseMove}
          onMouseLeave={handleStateMouseLeave}
          onClick={isMobile ? handleStateClick('NSW') : undefined}
          onTouchEnd={isMobile ? handleStateTouchEnd('NSW') : undefined}
        />
        {/* Victoria - bottom right */}
        <StateComponent
          Component={VIC_SVG}
          stateId="VIC"
          x={552}
          y={534}
          scale={1}
          isHovered={popover.state === 'VIC'}
          onMouseEnter={handleStateMouseEnter('VIC')}
          onMouseMove={handleStateMouseMove}
          onMouseLeave={handleStateMouseLeave}
          onClick={isMobile ? handleStateClick('VIC') : undefined}
          onTouchEnd={isMobile ? handleStateTouchEnd('VIC') : undefined}
        />
        {/* Tasmania - bottom island */}
        <StateComponent
          Component={TAS_SVG}
          stateId="TAS"
          x={610}
          y={652}
          scale={1}
          isHovered={popover.state === 'TAS'}
          onMouseEnter={handleStateMouseEnter('TAS')}
          onMouseMove={handleStateMouseMove}
          onMouseLeave={handleStateMouseLeave}
          onClick={isMobile ? handleStateClick('TAS') : undefined}
          onTouchEnd={isMobile ? handleStateTouchEnd('TAS') : undefined}
        />
        {/* New Zealand - far right */}
        <StateComponent
          Component={NZ_SVG}
          stateId="NZ"
          x={808}
          y={400}
          scale={1}
          isHovered={popover.state === 'NZ'}
          onMouseEnter={handleStateMouseEnter('NZ')}
          onMouseMove={handleStateMouseMove}
          onMouseLeave={handleStateMouseLeave}
          onClick={isMobile ? handleStateClick('NZ') : undefined}
          onTouchEnd={isMobile ? handleStateTouchEnd('NZ') : undefined}
        />
        {/* DOTS LAYER - All dots render after states to ensure proper layering */}
        {/* Western Australia Dots */}
        <DotsOverlay
          stateId="WA"
          x={-1}
          y={96}
          scale={1}
          isHovered={popover.state === 'WA'}
          DotsComponent={WA_DOTS_SVG}
          dotsTransform={statePositions.WA.dotsTransform}
        />
        {/* Queensland Dots */}
        <DotsOverlay
          stateId="QLD"
          x={494}
          y={22}
          scale={1}
          isHovered={popover.state === 'QLD'}
          DotsComponent={QLD_DOTS_SVG}
          dotsTransform={statePositions.QLD.dotsTransform}
        />
        {/* New South Wales Dots */}
        <DotsOverlay
          stateId="NSW"
          x={552}
          y={397}
          scale={1}
          isHovered={popover.state === 'NSW'}
          DotsComponent={NSW_DOTS_SVG}
          dotsTransform={statePositions.NSW.dotsTransform}
        />
        {/* Victoria Dots */}
        <DotsOverlay
          stateId="VIC"
          x={552}
          y={534}
          scale={1}
          isHovered={popover.state === 'VIC'}
          DotsComponent={VIC_DOTS_SVG}
          dotsTransform={statePositions.VIC.dotsTransform}
        />
        {/* Tasmania Dots */}
        <DotsOverlay
          stateId="TAS"
          x={667}
          y={700}
          scale={1}
          isHovered={popover.state === 'TAS'}
          DotsComponent={TAS_DOTS_SVG}
          dotsTransform={statePositions.TAS.dotsTransform}
        />
        {/* New Zealand Dots */}
        <DotsOverlay
          stateId="NZ"
          x={808}
          y={400}
          scale={1}
          isHovered={popover.state === 'NZ'}
          DotsComponent={NZ_DOTS_SVG}
          dotsTransform={statePositions.NZ.dotsTransform}
        />
      </svg>
      {renderPopoverContent()}
    </div>
  )
}
